import { MysqlTypeConnection } from "@connection";
import { BaseMysqlModel, Collection } from "@model";
import { StandardError } from "@errors";
import { FileLogger } from "@logger";
import { BaseResource } from "./_base.resource";
import { DeleteQuery, InsertQuery, Query, SelectQuery, UpdateQuery } from "./_query";
import { EQueryTypes } from "@types";
import { PoolConnection } from "mysql2/promise";

export abstract class BaseMysqlResource<T extends BaseMysqlModel, C extends Collection<T>> extends BaseResource {
	private connection: MysqlTypeConnection;
	public tableName: string;
	public primaryKeyField: string;
	protected transactionActive = false;
	protected transactionConnection: PoolConnection | null = null;

	constructor(connection: MysqlTypeConnection, tableName: string, primaryKeyField: string, { logger = null }: { logger?: FileLogger | null } = {}) {
		super({ logger });
		this.connection = connection;
		this.tableName = tableName;
		this.primaryKeyField = primaryKeyField;
	}

	abstract parse(modelData: any): T;

	abstract parseCollection(modelList: T[]): C;

	protected selectQuery() {
		return new SelectQuery(this.tableName);
	}
	protected insertQuery() {
		return new InsertQuery(this.tableName);
	}
	protected updateQuery() {
		return new UpdateQuery(this.tableName);
	}
	protected deleteQuery() {
		return new DeleteQuery(this.tableName);
	}

	/**
	 * @section BASE FUNCTIONS
	 * Functions to execute queries given previous set data
	 */

	private resultToTree(modelData: any) {
		const tree = Object.keys(modelData).reduce((carryTree: any, tableName: string) => {
			if (tableName.startsWith("__"))
				return carryTree;

			const path = tableName.split("__");
			const thisTableData = modelData[tableName];
			return this.appendToTree(carryTree, path, thisTableData);
		}, {});
		return tree;

	}

	appendToTree(currentTree: any, destinationPath: string[], newNode: any) {
		const firstKey = destinationPath.shift();
		if (firstKey) {
			if (!(firstKey in currentTree))
				currentTree[firstKey] = {};
			currentTree[firstKey] = this.appendToTree(currentTree[firstKey], destinationPath, newNode);
			return currentTree;
		}
		else
			return newNode;
	}

	extractExpressionsFromResults(resultList: any[]): { [alias: string]: any } {
		const expressions: { [alias: string]: any } = {};
		for (const resultItem of resultList)
			for (const [alias, value] of Object.entries(resultItem))
				if (alias.startsWith("__"))
					expressions[alias.replace("__", "")] = value;

		return expressions;
	}

	protected async executeQuery(query: Query, parseModel = true): Promise<C | { collection: C, expressions: { [alias: string]: any } }> {
		const { queryString, queryData, additionalParams } = query.getExecutionParams();
		const rawResults = await this.connection.exec(queryString, queryData, additionalParams);

		switch (query.queryType) {
			case EQueryTypes.select:
				if (rawResults.length) {
					const expressions = this.extractExpressionsFromResults(rawResults);
					const modelList = rawResults.map((modelRawResult: any) => {
						const modelItemTree = this.resultToTree(modelRawResult);
						return parseModel ? this.parse(modelItemTree) : modelItemTree;
					});
					return !Object.keys(expressions).length ? this.parseCollection(modelList) : { collection: this.parseCollection(modelList), expressions };
				}
				return this.parseCollection([]);
			case EQueryTypes.insert:
			case EQueryTypes.update:
			case EQueryTypes.delete:
				return rawResults;
			default:
				return Promise.reject(new StandardError(`Undefined query compiler for query of type ${typeof Query}`));
		}
	}

	public async transaction(process: CallableFunction) {
		await this.connection.beginTransaction();
		try {
			const results = await process();
			await this.connection.commitTransaction();
			return results;
		} catch (error) {
			await this.connection.rollbackTransaction();
			throw error;
		}
	}
}